import { ValidationResult } from '../ModalFieldRenderer';
import { toDayNumber, todayDayNumber } from '../../../utils/dateConversion';

// Common validation functions
export const validationHelpers = {
  required: (value: unknown): ValidationResult => ({
    isValid:
      value !== null && value !== undefined && String(value).trim() !== '',
    message: 'This field is required',
  }),

  email: (value: string): ValidationResult => {
    if (!value) return { isValid: true }; // Allow empty for optional fields
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return {
      isValid: emailRegex.test(value),
      message: 'Please enter a valid email address',
    };
  },

  minLength:
    (min: number) =>
    (value: unknown): ValidationResult => {
      const stringValue = String(value || '');
      return {
        isValid: !stringValue || stringValue.length >= min,
        message: `Must be at least ${min} characters long`,
      };
    },

  maxLength:
    (max: number) =>
    (value: unknown): ValidationResult => {
      const stringValue = String(value || '');
      return {
        isValid: !stringValue || stringValue.length <= max,
        message: `Must be no more than ${max} characters long`,
      };
    },

  numberRange:
    (min?: number, max?: number) =>
    (value: number): ValidationResult => {
      if (value === null || value === undefined) return { isValid: true };

      if (min !== undefined && value < min) {
        return { isValid: false, message: `Must be at least ${min}` };
      }

      if (max !== undefined && value > max) {
        return { isValid: false, message: `Must be no more than ${max}` };
      }

      return { isValid: true };
    },

  latitude: (value: number): ValidationResult => {
    if (value === null || value === undefined) return { isValid: true };
    return {
      isValid: value >= -90 && value <= 90,
      message: 'Latitude must be between -90 and 90 degrees',
    };
  },

  longitude: (value: number): ValidationResult => {
    if (value === null || value === undefined) return { isValid: true };
    return {
      isValid: value >= -180 && value <= 180,
      message: 'Longitude must be between -180 and 180 degrees',
    };
  },

  futureDate: (value: string): ValidationResult => {
    if (!value) return { isValid: true };
    const inputDay = toDayNumber(value);

    return {
      isValid: inputDay !== null && inputDay >= todayDayNumber(),
      message: 'Date must be today or in the future',
    };
  },

  pastDate: (value: string): ValidationResult => {
    if (!value) return { isValid: true };
    const inputDay = toDayNumber(value);

    return {
      isValid: inputDay !== null && inputDay <= todayDayNumber(),
      message: 'Date must be today or in the past',
    };
  },

  // Combine multiple validators
  combine:
    (...validators: Array<(value: unknown) => ValidationResult>) =>
    (value: unknown): ValidationResult => {
      for (const validator of validators) {
        const result = validator(value);
        if (!result.isValid) {
          return result;
        }
      }
      return { isValid: true };
    },
};
